/**
 * @fileoverview Display helpers for the Sales Performance screen.
 * @description Small pure helpers the Sales Performance components share: a month label for the
 * chart axes, a percentage that shows a dash when nothing was assigned, the colour of an
 * achievement bar, the chip colour of a pending target's status, and the CSV download that saves
 * the exported file in the browser.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  SalesPerformanceMonth,
  SalesPerformancePendingStatus,
  SalesPerformanceScopeKind,
} from '@/features/sales/types/sales-performance.types'

/** @description The Team dropdown's value for the whole selection. */
export const ALL_TEAMS = 'ALL'

/** @description Achievement at or above which a bar reads as strong. */
export const STRONG_ACHIEVEMENT = 80

/** @description Achievement at or above which a bar reads as on course. */
export const ON_COURSE_ACHIEVEMENT = 60

/** @description The chip colour of each pending status. */
export const PENDING_STATUS_CHIP_CLASS: Record<SalesPerformancePendingStatus, string> = {
  OVERDUE: 'bg-danger-bg text-danger',
  AT_RISK: 'bg-status-warning-bg text-status-warning',
  ON_TRACK: 'bg-success-bg text-teal',
  ON_HOLD: 'bg-input-bg text-slate',
}

/** @description The translation key of the scope line for each kind of scope. */
export const SCOPE_LABEL_KEY: Record<SalesPerformanceScopeKind, string> = {
  ORGANISATION: 'sales.salesPerformance.scope.ORGANISATION',
  TEAM: 'sales.salesPerformance.scope.TEAM',
  SELF: 'sales.salesPerformance.scope.SELF',
}

/**
 * @description The short month name of a YYYY-MM month, for a chart axis, in the language the
 * screen is shown in.
 * @param {string} month - The month, e.g. `2026-04`.
 * @param {string} [language] - The UI language (e.g. `hi`); English when omitted.
 * @returns {string} The name, e.g. `Apr`.
 */
export function monthLabel(month: string, language = 'en-IN'): string {
  return new Intl.DateTimeFormat(language, { month: 'short', timeZone: 'UTC' }).format(
    new Date(`${month}-01T00:00:00Z`),
  )
}

/**
 * @description Turns the summary's months into the points of one monthly chart.
 * @param {SalesPerformanceMonth[]} months - The summary's months.
 * @param {'achieved' | 'onboarded'} series - Which of the two series to draw.
 * @param {string} [language] - The UI language the labels are written in.
 * @returns {{ label: string; value: number }[]} One point per month, labelled with its short name.
 */
export function monthPoints(
  months: SalesPerformanceMonth[],
  series: 'achieved' | 'onboarded',
  language?: string,
): { label: string; value: number }[] {
  return months.map((month) => ({
    label: monthLabel(month.month, language),
    value: month[series],
  }))
}

/**
 * @description Formats an achievement percentage, or a dash when nothing was assigned.
 * @param {number | null} percent - The percentage, or null when nothing was assigned.
 * @returns {string} The text to show.
 */
export function formatPercent(percent: number | null): string {
  return percent === null ? '—' : `${String(percent)}%`
}

/**
 * @description The fill colour of an achievement bar: strong, on course, or behind.
 * @param {number | null} percent - The percentage, or null when nothing was assigned.
 * @returns {string} The Tailwind background class.
 */
export function achievementBarClass(percent: number | null): string {
  if (percent !== null && percent >= STRONG_ACHIEVEMENT) return 'bg-teal'
  return percent !== null && percent >= ON_COURSE_ACHIEVEMENT ? 'bg-info' : 'bg-danger'
}

/**
 * @description Saves CSV text as a file through the browser. A byte order mark is put back at the
 * start (reading the response as text drops it) so a spreadsheet opens non-ASCII text correctly.
 * @param {string} filename - The file name to save as.
 * @param {string} csv - The CSV text.
 */
export function downloadCsv(filename: string, csv: string): void {
  const body = csv.startsWith('\uFEFF') ? csv : `\uFEFF${csv}`
  const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8;' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
